import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import Scrollbars from "react-custom-scrollbars-2";

import {
    convertClipboardTextIntoCells,
    copyTextToClipboard,
    readTextFromClipboard,
} from "../../../../lib/clipboardUtils";
import {
    COLUMN_INDEX_TO_NAME,
    NUMBER_OF_CELLS_PER_LINE,
    TransactionProxyObject,
} from "../AddTransactionsModal";
import {
    CellGrid,
    EditableCellItem,
    getCutColumns,
    getFirstCellFromSelection,
    getOnChangeCallbacks,
    getOnClickCallbacks,
    getSelectedItemsWithSelectionBorders,
    recalculateSelectableItemsCallback,
    traverseCellsToEdit,
} from "../../../accounting/editableTable/EditableTableUtils";
import { useEditableTableContext } from "../../../accounting/editableTable/EditableTableContext";
import { EditableTableCellSelectionState } from "../../../accounting/editableTable/EditableTableCell";
import coreStyles from "../../../accounting/editableTable/EditableTable.module.scss";
import { EditableTableBodyCore } from "../../../accounting/editableTable/EditableTableBodyCore";
import styles from "./Table.module.scss";
import { Row } from "./Row";

interface Props {
    tableRef: React.RefObject<HTMLDivElement>;
    scrollbarsRef: React.RefObject<Scrollbars>;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
    ) => void;
    // NOTE: for now we support only one type of search options
    // (assuming all searchable columns have the same type)
}

export type JournalEntryTableLineStateValue = string | number | null;

export const DRAGGING_THRESHOLD = 2;

export const Body = ({ tableRef, scrollbarsRef, setFieldValue }: Props) => {
    const selectableItemsRef = useRef<CellGrid<EditableCellItem>>([]);
    const {
        selectedArea,
        setSelectedArea,
        targetLines,
        isInBuffer,
        setIsInBuffer,
        updateAndReturnNewLine,
    } = useEditableTableContext<TransactionProxyObject>();
    const linesRef = useRef(targetLines);
    const [lineToInsert, setLineToInsert] = useState<number | null>(null);

    const recalculateSelectableItems = useCallback(
        () =>
            recalculateSelectableItemsCallback(
                tableRef,
                selectableItemsRef,
                coreStyles.editableTableCell,
            ),
        [tableRef],
    );

    useEffect(() => {
        linesRef.current = targetLines;
        recalculateSelectableItems();
    }, [targetLines, recalculateSelectableItems]);

    const previousSelectedItemsWithSelectionBorders = useRef<
        CellGrid<EditableTableCellSelectionState>
    >([]);

    const selectedItemsWithSelectionBorders: CellGrid<EditableTableCellSelectionState> =
        useMemo(
            () =>
                getSelectedItemsWithSelectionBorders({
                    selectedArea,
                    isInBuffer,
                    previousSelectedItemsWithSelectionBorders,
                    numberOfCellsPerLine: NUMBER_OF_CELLS_PER_LINE,
                    selectableItemsRef,
                }),
            [selectedArea, isInBuffer],
        );

    const editLineValue = useCallback(
        (
            lineIndex: number,
            field: keyof TransactionProxyObject,
            value: string | number | null,
        ) => {
            const newLine = updateAndReturnNewLine({
                line: linesRef.current[lineIndex],
                changes: [{ field, value }],
                fillEmptyFieldsAutomatically: true,
            });

            setFieldValue(`transactions[${lineIndex}]`, newLine);
        },
        [setFieldValue, updateAndReturnNewLine],
    );

    const clearLine = useCallback(
        (lineIndex: number) => {
            const newLine = updateAndReturnNewLine({
                line: linesRef.current[lineIndex],
                changes: [
                    { field: "description", value: "" },
                    { field: "category", value: "" },
                    { field: "amount", value: null },
                    { field: "date", value: "" },
                ],
                fillEmptyFieldsAutomatically: true,
            });

            setFieldValue(`transactions.${lineIndex}`, newLine);
        },
        [setFieldValue, updateAndReturnNewLine],
    );

    const memoizedOnClickCallback = useMemo(
        () =>
            getOnClickCallbacks(
                setSelectedArea,
                NUMBER_OF_CELLS_PER_LINE,
                targetLines.length,
            ),
        [targetLines.length, setSelectedArea],
    );

    const memoizedOnChangeCallback: CellGrid<
        (value: JournalEntryTableLineStateValue) => void
    > = useMemo(
        () =>
            getOnChangeCallbacks({
                targetLines,
                editLineValue,
                columnIndexToName: COLUMN_INDEX_TO_NAME,
                numberOfCellsPerLine: NUMBER_OF_CELLS_PER_LINE,
            }),
        [editLineValue, targetLines],
    );

    const onPaste = useCallback(
        (e: any) => {
            if (!selectedArea) {
                return;
            }
            e.preventDefault();

            readTextFromClipboard().then((text) => {
                if (text === null) {
                    return;
                }
                const cellsToInsert = convertClipboardTextIntoCells(text || "");

                const newLines = [...linesRef.current];

                traverseCellsToEdit({
                    cellsToInsert,
                    lastColumnIndex: NUMBER_OF_CELLS_PER_LINE - 1,
                    firstActiveCell: getFirstCellFromSelection(selectedArea),
                    callback: (lineIndex, columnIndex, value) => {
                        const columnToChangeName =
                            COLUMN_INDEX_TO_NAME[columnIndex];
                        if (!columnToChangeName || value === undefined) {
                            return;
                        }
                        const valueToInsert: JournalEntryTableLineStateValue =
                            value;

                        newLines[lineIndex] = updateAndReturnNewLine({
                            line: newLines[lineIndex],
                            changes: [
                                {
                                    field: columnToChangeName,
                                    value: valueToInsert,
                                },
                            ],
                        });
                    },
                });
                setFieldValue(`transactions`, newLines);
                setIsInBuffer(false);
            });
        },
        [
            selectedArea,
            setFieldValue,
            setIsInBuffer,
            linesRef,
            updateAndReturnNewLine,
        ],
    );

    const onCut = useCallback(
        (e: any) => {
            if (!selectedArea) {
                return;
            }
            e.preventDefault();

            // NOTE: we don't want to copy or change non-editable cells
            const { effectiveToColumn, cells } = getCutColumns(
                linesRef,
                selectedArea,
                COLUMN_INDEX_TO_NAME,
            );

            copyTextToClipboard(
                cells.map((row) => row.join("	")).join("\n"),
            ).then((result) => {
                if (!result) {
                    return;
                }
                const newLines = [...linesRef.current];

                traverseCellsToEdit({
                    cellsToInsert: cells,
                    lastColumnIndex: effectiveToColumn,
                    firstActiveCell: [
                        selectedArea.topLeftCellPosition.rowIndex,
                        selectedArea.topLeftCellPosition.columnIndex,
                    ],
                    callback: (lineIndex, columnIndex) => {
                        const columnToChangeName =
                            COLUMN_INDEX_TO_NAME[columnIndex];
                        if (!columnToChangeName) {
                            return;
                        }
                        (newLines[lineIndex] as any)[columnToChangeName] = "";
                    },
                });
                setFieldValue(`transactions`, newLines);
                setIsInBuffer(false);
            });
        },
        [selectedArea, setIsInBuffer, setFieldValue, linesRef],
    );

    const hasSelectedAllLines =
        selectedArea?.topLeftCellPosition.rowIndex === 0 &&
        selectedArea?.bottomRightCellPosition.rowIndex ===
            targetLines.length - 1;

    return (
        <EditableTableBodyCore<TransactionProxyObject>
            onCut={onCut}
            onPaste={onPaste}
            formValueName="transactions"
            isEditable={true}
            tableRef={tableRef}
            scrollbarsRef={scrollbarsRef}
            setFieldValue={setFieldValue}
            lineToInsert={lineToInsert}
            setLineToInsert={setLineToInsert}
            columnIndexToName={COLUMN_INDEX_TO_NAME}
        >
            <div ref={tableRef} className={styles.editableTableBody}>
                {targetLines.map(({ id }, lineIndex) => (
                    <Row
                        key={id}
                        id={id}
                        lineIndex={lineIndex}
                        hasSelectedAllLines={hasSelectedAllLines}
                        onChangeCallback={memoizedOnChangeCallback[lineIndex]}
                        onClickCallback={memoizedOnClickCallback[lineIndex]}
                        selectionState={
                            selectedItemsWithSelectionBorders[lineIndex]
                        }
                        insertToThisLine={lineToInsert === lineIndex}
                        clearLine={() => clearLine(lineIndex)}
                    />
                ))}
            </div>
        </EditableTableBodyCore>
    );
};
